import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, DataTable, FilterBar, EmptyState, PageHeader, Tabs } from '@/components/ui';
import { ApiUsageLogs } from './ApiUsageLogs';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { superApi } from '@/shared/api/superApi';
import { downloadCsv } from '@/shared/lib/downloadCsv';
import { toast } from '@/components/ui/Toast';
import { useResource } from '@/shared/hooks/useResource';

interface AuditRow { id: number; action: string; target_type?: string; target_id?: string; user_id?: number; tenant_id?: number; ip?: string; created_at: string }

export function Audit() {
  const [q, setQ] = useState('');
  // Honor deep-link filters (?tenant_id=, ?user_id=) so drill-downs from the
  // Tenants / users screens actually scope the log instead of showing all.
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant_id') || undefined;
  const userId = searchParams.get('user_id') || undefined;
  // לשוניות: לוג אירועים פנימי / לוגי שימוש ב-API ו-Webhooks (deep-link ?tab=usage).
  const tab = searchParams.get('tab') === 'usage' ? 'usage' : 'audit';

  const fetcher = useCallback(
    () => superApi.audit({ action: q || undefined, tenant_id: tenantId, user_id: userId })
      .then((res) => res.data as AuditRow[]),
    [q, tenantId, userId]
  );
  const { data, loading, error, reload } = useResource(fetcher, { deps: [q, tenantId, userId] });
  const rows = data ?? [];

  const cols: DataTableColumn<AuditRow>[] = [
    { key: 'created_at', he: 'מתי', render: (r) => new Date(r.created_at).toLocaleString('he-IL') },
    { key: 'action', he: 'פעולה' },
    { key: 'target_type', he: 'סוג' },
    { key: 'target_id', he: 'מזהה' },
    { key: 'user_id', he: 'משתמש', render: (r) => r.user_id != null ? <span dir="ltr">{r.user_id}</span> : '—' },
    { key: 'tenant_id', he: 'לקוח', render: (r) => r.tenant_id != null ? <span dir="ltr">{r.tenant_id}</span> : '—' },
    { key: 'ip', he: 'IP', render: (r) => r.ip ? <span dir="ltr">{r.ip}</span> : '—' },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="לוג אירועים" actions={tab === 'audit' ? <>
        <button
          type="button"
          className="btn"
          onClick={() =>
            downloadCsv('/super/audit/export', { format: 'csv', tenant_id: tenantId, user_id: userId }, 'merkaziya-audit.csv')
              .catch(() => toast('שגיאה בייצוא', 'danger'))
          }
        >
          ייצא CSV
        </button>
      </> : undefined} />
      <Tabs
        value={tab}
        onChange={(id) => {
          const next = new URLSearchParams(searchParams);
          if (id === 'usage') next.set('tab', 'usage'); else next.delete('tab');
          setSearchParams(next, { replace: true });
        }}
        items={[
          { id: 'audit', label: 'פעולות במערכת' },
          { id: 'usage', label: 'שימוש ב-API ו-Webhooks' },
        ]}
      />
      {tab === 'usage' ? (
        <ApiUsageLogs />
      ) : (
        <>
          <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} placeholder="חיפוש לפי פעולה…" />
          {error && rows.length === 0 ? (
            <Card>
              <EmptyState
                icon="warn"
                title="שגיאה בטעינת לוג האירועים"
                description="לא ניתן היה לטעון את הלוג מהשרת."
                action={<button type="button" className="btn primary" onClick={reload}>נסה שוב</button>}
              />
            </Card>
          ) : (
            <Card padded={false}><DataTable<AuditRow> columns={cols} rows={rows} loading={loading} pageSize={50} /></Card>
          )}
        </>
      )}
    </div>
  );
}
